
import { IJourney } from "@interfaces/IJourney";
import { IPlot } from "@interfaces/IPlot";
import { ISensorData } from "@interfaces/ISensorData";
import * as angular from "angular";
import * as moment from "moment";
import { IPlannedRoute } from "src/app/main/components/planning/interfaces/IPlannedRoute";
import { ReasonCode } from "../enum/ReasonCode";
import { SpecialUserPermissions } from "../enum/SpecialUserPermissions";
import { AccountFeatureService } from "./accountFeature.service";
import { ConstantsService } from "./constantsService";
import { GoogleMarkerService } from "./googleMarker.service";
import { MapService } from "./map.service";
import { ReasonCodeService } from "./reasonCode.service";
import { UsersService } from "./users.service";
import { VeriDate } from "./veriDate.service";

export class PlotService {

    private directionArray: string[] = ["North", "North East", "East", "South East", "South", "South West", "West", "North West"];
    isDemoSite: boolean;

    static $inject = ["$q", "$http", "$log", "$filter", "veriDate", "reasonCodeService", "googleMarkerService", "usersService", "accountFeatureService", "mapService", "constantsService"];

    constructor(
        private $q: angular.IQService,
        private $http: angular.IHttpService,
        private $log: angular.ILogService,
        private $filter: angular.IFilterService,
        private veriDate: VeriDate,
        private reasonCodeService: ReasonCodeService,
        private googleMarkerService: GoogleMarkerService,
        private readonly usersService: UsersService,
        private readonly accountFeatureService: AccountFeatureService,
        private readonly mapService: MapService,
        private readonly constantsService: ConstantsService
    ) {
        this.isDemoSite = this.constantsService.getIsDemoSite();
    }

    getPlotsForRoute(route: IPlannedRoute): angular.IPromise<IPlot[]> {
        if (!this.usersService.isCurrentUserOnFoundationPlatform() && !this.accountFeatureService.doesCurrentUserHaveSpecialPermission(SpecialUserPermissions.ShowHistoricalPlots)) {
            return this.$q.reject();
        }

        if (route.VehicleId) {
            return this.getPlotsForDateRange(route.VehicleId, this.veriDate.createMoment(route.PlannedStartTime).subtract("h", 4), this.veriDate.createMoment(route.PlannedEndTime).add("h", 4));
        }

        if (route.DriverId) {
            return this.getPlotsForDateRangeByDriver(route.DriverId, this.veriDate.createMoment(route.PlannedStartTime).subtract("h", 4), this.veriDate.createMoment(route.PlannedEndTime).add("h", 4));
        }
    }

    getPlotsForJourney(vehicleId: number, journey: IJourney): angular.IPromise<IPlot[]> {
        if (!this.usersService.isCurrentUserOnFoundationPlatform() && !this.accountFeatureService.doesCurrentUserHaveSpecialPermission(SpecialUserPermissions.ShowHistoricalPlots)) {
            return this.$q.reject();
        }

        return this.getPlotsForDateRange(vehicleId, moment(journey.StartUtc), moment(journey.EndUtc).add(1, "second"));
    }

    getPlotsForDate(vehicleId: number, date: moment.Moment | Date): angular.IPromise<IPlot[]> {
        let newDate: moment.Moment = moment(date);

        const startDate = newDate.clone();
        const endDate = newDate.clone();

        startDate.startOf("day");
        endDate.add(1, "day").startOf("day");

        return this.getPlotsForDateRange(vehicleId, startDate, endDate);
    }

    getPlotNearestToTime(vehicleId: number, start: moment.Moment): angular.IPromise<IPlot> {

        const startTime = start.clone();
        const endTime = start.clone();

        startTime.startOf("hour");
        endTime.add(1, "hour").startOf("hour");

        return this.getPlotsForDateRange(vehicleId, startTime, endTime)
            .then(plots => { return plots[0] });
    }

    getPlotsForDateRange(vehicleId: number, start: moment.Moment | Date, end: moment.Moment | Date): angular.IPromise<IPlot[]> {
        if (!this.usersService.isCurrentUserOnFoundationPlatform() && !this.accountFeatureService.doesCurrentUserHaveSpecialPermission(SpecialUserPermissions.ShowHistoricalPlots)) {
            return this.$q.reject();
        }

        return this
            .$http({
                method: "GET",
                url: `/api/vehicles/${vehicleId}/plots?plotOption=Basic&start=${start.toISOString()}&end=${end.toISOString()}`
            })
            .then((result: angular.IHttpResponse<IPlot[]>) => {

                this.preparePlotsForMap(result.data);

                return result.data;
            });
    }

    getPlotsForDateRangeByDriver(driverId: number, start: moment.Moment, end: moment.Moment): angular.IPromise<IPlot[]> {
        return this
            .$http({
                method: "GET",
                url: `/api/drivers/${driverId}/plots?plotOption=Basic&start=${start.toISOString()}&end=${end.toISOString()}`
            }).then((result: angular.IHttpResponse<IPlot[]>) => {
                this.preparePlotsForMap(result.data);

                return result.data;
            });

    }

    getSensorsData(vehicleId: number, start: moment.Moment, end: moment.Moment): angular.IPromise<ISensorData[]> {

        return this.$http({
            method: "GET",
            url: `/api/sensorData/${vehicleId}?start=${start.toISOString()}&end=${end.toISOString()}`,
        }).then((result: angular.IHttpResponse<ISensorData[]>) => {
            // this.preparePlotsForMap(result.data);

            return result.data;
        });
    }

    getLatestPlots(): angular.IPromise<IPlot[]> {
        var params: any = {
            /*minsToLive: 1 */
            plotOption: "Basic",
        };

        // Removed for now as it was causing issues
        //if (excludePlotsBefore) {
        //    params.excludePlotsBefore = excludePlotsBefore.toISOString();
        //}

        return this
            .$http({
                method: "GET",
                url: `/api/vehicles/plots/latest`,
                params: params
            })
            .then((result: angular.IHttpResponse<IPlot[]>) => {
                this.preparePlotsForMap(result.data, true);

                return result.data;
            });
    }

    getLatestPlotForVehicle(vehicleId: number): angular.IPromise<IPlot> {
        return this.$http({
            method: "GET",
            url: `/api/vehicles/${vehicleId}/plots/latest?plotOption=Basic`
        }).then((result: angular.IHttpResponse<IPlot>) => {
            if (!result.data) {
                return null;
            }

            this.preparePlotsForMap([result.data], true);

            return result.data;
        });
    }

    getPlot(vehicleId: number, plotId: number): angular.IPromise<IPlot> {
        var params: any = { minsToLive: 60, plotOption: "Basic" };

        return this
            .$http({
                method: "GET",
                url: `/api/vehicles/${vehicleId}/plots/${plotId}`,
                params: params
            })
            .then((result: angular.IHttpResponse<IPlot>) => {
                this.preparePlotForMap(result.data);

                return result.data;
            });
    }

    isPlotUtilised(reasonCode: ReasonCode): boolean {

        const notUtilised = reasonCode === ReasonCode.IgnitionOff || reasonCode === ReasonCode.Wakeup;
        return !notUtilised;
    }

    getUtilisationColor(utilised: boolean): string {
        return utilised ? "#039be5" : "#cccccc";
    }

    simplifyPlots(plots: IPlot[], includePlot?: IPlot, tolerance = 0.0001): IPlot[] {

        // Convert our plots into number[][] format
        const locationArray: number[][] = [];
        for (var i = 0; i < plots.length; i++) {
            const plot = plots[i];
            locationArray.push([plot.Latitude, plot.Longitude, plot.Id]);
        }

        const simplifiedArray = this.mapService.simplifyRoute(locationArray, tolerance, true);
        //console.log(locationArray.length, ">", simplifiedArray.length, tolerance);

        var newPlotList: IPlot[] = [];
        let simplifiedArrayIndex = 0;

        for (var i = 0; i < plots.length; i++) {
            const p = plots[i];

            if (p.Id === simplifiedArray[simplifiedArrayIndex][2]) {
                newPlotList.push(p);
                simplifiedArrayIndex++;
            }
        }

        return newPlotList;
    }

    getVisisblePlotsBasedOnZoomLevel(plots: IPlot[], map: google.maps.Map, selectedPlot?: IPlot): angular.IPromise<IPlot[]> {
        if (!plots || !map) {
            return this.$q.when([]);
        }

        performance.mark("getVisisblePlotsBasedOnZoomLevel");
        const newPlotList: IPlot[] = [];

        var markerWidth = 30;

        var projection = map.getProjection();
        var lastPoint: google.maps.Point = null;

        var scale = Math.pow(2, map.getZoom());
        var nw = new google.maps.LatLng(map.getBounds().getNorthEast().lat(), map.getBounds().getSouthWest().lng());
        var worldCoordinateNW = projection.fromLatLngToPoint(nw);

        plots.forEach(plot => {
            var worldCoordinate = projection.fromLatLngToPoint(new google.maps.LatLng(plot.Latitude, plot.Longitude));
            var pixelOffset = new google.maps.Point(
                Math.floor((worldCoordinate.x - worldCoordinateNW.x) * scale),
                Math.floor((worldCoordinate.y - worldCoordinateNW.y) * scale)
            );

            var recordLastPoint = !lastPoint;

            if (lastPoint != null) {
                var xPixels = pixelOffset.x - lastPoint.x;
                var yPixels = pixelOffset.y - lastPoint.y;

                var pixelsDifference = Math.sqrt(xPixels * xPixels + yPixels * yPixels);

                if (pixelsDifference > markerWidth) {
                    recordLastPoint = true;
                }
            }

            if (recordLastPoint) {
                newPlotList.push(plot);
                lastPoint = pixelOffset;
            }
        });

        performance.measure("getVisisblePlotsBasedOnZoomLevel");

        var promiseArray: angular.IPromise<any>[] = [];

        for (var i = 0; i < newPlotList.length; i++) {
            const plot = newPlotList[i];

            var promise = this.googleMarkerService.generateGoogleMapsMarkerOptionsForPlot(plot, plot == selectedPlot).then(options => {
                plot.markerModel.options = options;
            });

            promiseArray.push(promise);
        }

        return this.$q.all(promiseArray).then(() => {
            return newPlotList;
        });
    }

    findPlotClosestToLatLng(plots: IPlot[], latLng: google.maps.LatLng) {
        if (!plots) {
            return null;
        }

        // Find closest point
        var closestPlot: IPlot;
        var closestPlotDistance = Number.MAX_VALUE;

        for (var i = 0; i < plots.length; i++) {
            const p = plots[i];

            var plotLatLng = new google.maps.LatLng(p.Latitude, p.Longitude);
            var distance = google.maps.geometry.spherical.computeDistanceBetween(latLng, plotLatLng);

            if (distance < closestPlotDistance) {
                closestPlot = p;
                closestPlotDistance = distance;
            }
        }

        return closestPlot;
    }

    findPlotClosestToTime(time: moment.Moment, plots: IPlot[]) {

        var closestPlot: IPlot;

        for (var i = 0; i < plots.length; i++) {
            var plot = plots[i];

            if (closestPlot && plot.UtcMoment.isAfter(time)) {
                if (plot.UtcMoment.diff(time, "s") < closestPlot.UtcMoment.diff(time, "s")) {
                    closestPlot = plot;
                }

                break;
            }

            closestPlot = plot;
        }

        return closestPlot;
    }

    findPlotById(id: number, plots: IPlot[]) {
        for (var i = 0; i < plots.length; i++) {
            var plot = plots[i];

            if (plot.Id == id) {
                return plot;
            }
        }

        return null;
    }

    arePlotsCloserThan(plot1: IPlot, plot2: IPlot, minDistance: number): boolean {
        //Decimal Places   Aprox.Distance    Say What?
        //1                10 kilometers      6.2 miles
        //2                1 kilometer        0.62 miles
        //3                100 meters         About 328 feet
        //4                10 meters          About 33 feet
        //5                1 meter            About 3 feet
        //6                10 centimeters     About 4 inches
        //7                1.0 centimeter     About ½ an inch
        //8                1.0 millimeter     The width of paperclip wire.
        //9                0.1 millimeter     The width of a strand of hair.
        //10               10 microns         A speck of pollen.
        //11               1.0 micron         A piece of cigarette smoke.
        //12               0.1 micron         You're doing virus-level mapping at this point.
        //13               10 nanometers      Does it matter how big this is?
        //14               1.0 nanometer      Your fingernail grows about this far in one second.
        //15               0.1 nanometer      An atom.An atom! What are you mapping?
        //var minDifference = 0.001; // 100 metre

        return (Math.abs(plot1.Latitude - plot2.Latitude) < minDistance && Math.abs(plot1.Longitude - plot2.Longitude) < minDistance);
    }

    getColorForSpeed(speedMph: number): string {

        var colour: string = "red";

        var speedKph = speedMph * 1.60934;

        if (speedKph <= 40) {
            //25 mph
            colour = "darkgrey";
        }

        else if (speedKph <= 50) {
            //31 mph
            colour = "darkgrey";
        }

        else if (speedKph <= 60) {
            //37 mph
            colour = "green";
        }

        else if (speedKph <= 80) {
            // 50 mph
            colour = "darkorange";
        }

        else if (speedKph <= 100) {
            // 62 mph
            colour = "red";
        }

        return colour;
    }

    slicePlotArrayByTime(plots: IPlot[], startDate?: string, endDate?: string): IPlot[] {
        if (!(startDate && endDate)) {
            return plots;
        }

        const plotList: IPlot[] = [];

        const startDateMoment = this.veriDate.createMoment(startDate);
        const endDateMoment = this.veriDate.createMoment(endDate);

        for (let i = 0; i < plots.length; i++) {
            const plot = plots[i];

            var dateValue = plot.UtcMoment.valueOf();
            const pointInRange = dateValue >= startDateMoment.valueOf() && dateValue <= endDateMoment.valueOf();

            if (pointInRange) {
                plotList.push(plot);
            }
        }

        return plotList;
    }

    convertDirectionToWords(degrees: number): string {

        const maxDirectionArrows = 8;
        const simpleNumber = (degrees / (360 / maxDirectionArrows));
        const val = Math.floor(simpleNumber + 0.5);

        const index = val % maxDirectionArrows;
        const arrayIndex = parseInt(index.toString(), 0);
        const compaseDirection = this.directionArray[arrayIndex];

        return compaseDirection;
    }

    setPlotSelectedClass(plot: IPlot, selected: boolean) {
        if (plot.markerModel.options == null) {
            console.error("No options object found on Plot", plot);
            return;
        }

        // Todo: Remove
        plot.markerModel.options.labelClass = "marker-labels";
        if (selected) {
            plot.markerModel.options.labelClass += " selected";
            plot.markerModel.options.optimized = false;
        }
    }

    //setPlotSelectionClass(model: any, modelId: number, plot: IPlot, selected: boolean, control: any) {
    //    if (!plot) {
    //        return;
    //    }

    //    plot.selected = selected;

    //    if (!model || !plot || !plot.options) {
    //        return;
    //    }

    //    this.setPlotSelectedClass(plot, selected);

    //    if (control == null) {
    //        console.error("setPlotSelectionClass > 'control' not defined", model, modelId, plot, selected);
    //        return;
    //    }

    //    const markerModel = this.updateMapMarkerModel(model, modelId, control);
    //    if (markerModel) {
    //        const clusterer = markerModel.gManager.clusterer;

    //        this.setClusterSelectionClass(clusterer, modelId, selected);
    //    }
    //}

    setClusterSelectionClass(clusterer: any, modelId: number, selected: boolean) {
        if (!clusterer) {
            return;
        }

        clusterer
            .getClusters()
            .forEach(c => {
                var markers = c.getMarkers();
                if (markers.length > 0) {
                    if (markers.get(modelId) && c.clusterIcon_.div_) {
                        // Found marker in cluster
                        c.clusterIcon_.div_.className = "cluster";

                        if (selected) {
                            c.clusterIcon_.div_.className += " selected";
                        }
                    }
                }
            });
    }

    isMoving(plot: IPlot): boolean {
        let isMoving = false;

        if (plot.Velocity !== 0 && plot.Reason != ReasonCode.Wakeup) {
            isMoving = true;
        }

        return isMoving;
    }

    //getMapMarkerModel(id: number, imarkermodel): IMarkerModelMap {
    //    if (this.mode) {
    //        //console.error("getMapMarkerModel failed as no control is set", control);
    //        return null;
    //    }

    //    const marker = control.getPlurals().get(id);
    //    return marker;
    //}

    //private updateMapMarkerModel(model: any, id: number, control: any): any {
    //    if (model) {
    //        const marker = this.getMapMarkerModel(id, control);

    //        if (marker) {
    //            marker.updateModel(model);
    //            return marker;
    //        }
    //    }

    //    return null;
    //}

    preparePlotForMap(plot: IPlot) {
        if (!plot) {
            return;
        }

        //plot.coords = new CoordsModel(plot.Latitude, plot.Longitude);
        plot.markerModel = {
            id: plot.Id,
            options: this.googleMarkerService.createEventMarkerOptions({ label: "", position: { lat: plot.Latitude, lng: plot.Longitude } })
        }

        if (this.isDemoSite && plot.LocationInformation?.GeofenceIds?.length > 0) {
            plot.LocationInformation.LocationDescription = "Demo Geofence";
        }

        // Temp for testing
        //if (!dontCreateOptions) {
        //    plot.options = this.veriMarkerService.generateMarkerOptions(plot);
        //}

        //plot.options.title = plot.FormattedDate;

        // This data is useful for side navigation  display
        if (plot.Utc) {
            // Small optimisation because we know plot Utcs come down in ISO format i.e. "2017-09-04T09:30:09Z"
            plot.UtcMoment = this.veriDate.createMoment(plot.Utc);
            plot.FormattedDate = this.veriDate.formatDate(plot.UtcMoment);
        }
        else {
            console.warn(`Plot for ${plot.VehicleId} has no Utc '${plot.Utc}'`);
        }

        // Filter out 999 temp plots
        //if (plot.TemperatureRecords) {
        //    for (var i = 0; i < plot.TemperatureRecords.length; i++) {
        //        const temp = plot.TemperatureRecords[i];
        //        temp.invalid = temp.Validity != 0 || temp.Temperature === 999 || temp.Temperature === 85;
        //    }
        //}
    }

    findPlot(plots: IPlot[], plotId: number): IPlot {
        for (let i = 0; i < plots.length; i++) {
            const plot = plots[i];

            if (plot.Id === plotId) {
                return plot;
            }
        }

        return null;
    }

    findMatchingPlot(startTime: moment.Moment, endTime: moment.Moment, plots: IPlot[]): IPlot {
        let matchingPlot: IPlot;

        let lastPlot: IPlot;

        for (let i = 0; i < plots.length; i++) {
            const plot = plots[i];

            if (!lastPlot) {
                lastPlot = plot;
                continue;
            }

            if (startTime.isBetween(lastPlot.UtcMoment, plot.UtcMoment)) {

                const lastDiff = startTime.diff(lastPlot.UtcMoment);
                const nextDiff = plot.UtcMoment.diff(endTime);

                if (lastDiff < nextDiff) {
                    matchingPlot = lastPlot;
                }
                else {
                    matchingPlot = plot;
                }

                break;
            }

            lastPlot = plot;
        }

        return matchingPlot;
    }


    preparePlotsForMap(plots: IPlot[], dontCreateOptions = false): void {

        if (!plots) {
            return;
        }

        for (let i = 0; i < plots.length; i++) {
            const plot = plots[i];
            plot.ReasonText = this.reasonCodeService.getReasonCodeLabel(plot.Reason);
            //plot.FormattedAddress = null;

            this.preparePlotForMap(plot);
        }

        // Plots are out of order, reverse for highcharts and display in correct (ascending time) order
        plots.sort((a, b) => { return a.UtcMoment.valueOf() - b.UtcMoment.valueOf(); });
        //plots.sort((a, b) => { return a.UtcMoment.valueOf() < b.UtcMoment.valueOf() ? -1 : 1; });
    }

    private removeSimilarPoints(plots: IPlot[]): IPlot[] {
        var filteredPlots: IPlot[] = [];

        const speedDifference = 5;
        var latLngDifference = 0.05;

        var previousIncludedPlot: IPlot = null;

        for (var i = 0; i < plots.length; i++) {
            const plot = plots[i];

            var includePlot = false;

            if (previousIncludedPlot) {
                if (plot.Reason !== previousIncludedPlot.Reason) {
                    includePlot = true;
                }
                else if (Math.abs(plot.Velocity - previousIncludedPlot.Velocity) > speedDifference) {
                    includePlot = true;
                }
                else if (Math.abs(plot.Latitude - previousIncludedPlot.Latitude) > latLngDifference) {
                    includePlot = true;
                }
                else if (Math.abs(plot.Longitude - previousIncludedPlot.Longitude) > latLngDifference) {
                    includePlot = true;
                }
            }
            else {
                includePlot = true;
            }

            if (includePlot) {
                filteredPlots.push(plot);
                previousIncludedPlot = plot;
            }
        }

        return filteredPlots;
    }

    //myPaths: google.maps.Polyline[];

    //private ProcessColouredPlotData(vm: IVmPanelPlot, vehicle: IVehicle): void {

    //    $(`<span id="map-tooltip">WHAT</span>`).appendTo("#map_canvas");

    //    var mouseover = () => { };

    //    this.myPaths = [];

    //    console.log("ProcessPlotData");

    //    var gMap = vm.map.control.getGMap();

    //    // Setup map style

    //    var styles = [
    //        {
    //            "featureType": "administrative",
    //            "elementType": "labels.text.fill",
    //            "stylers": [{ "color": "#6195a0" }]
    //        }, { "featureType": "landscape", "elementType": "all", "stylers": [{ "color": "#f2f2f2" }] },
    //        {
    //            "featureType": "landscape",
    //            "elementType": "geometry.fill",
    //            "stylers": [{ "color": "#ffffff" }]
    //        },
    //        { "featureType": "poi", "elementType": "all", "stylers": [{ "visibility": "off" }] },
    //        {
    //            "featureType": "poi.park",
    //            "elementType": "geometry.fill",
    //            "stylers": [{ "color": "#e6f3d6" }, { "visibility": "on" }]
    //        }, {
    //            "featureType": "road",
    //            "elementType": "all",
    //            "stylers": [{ "saturation": -100 }, { "lightness": 45 }, { "visibility": "simplified" }]
    //        }, {
    //            "featureType": "road.highway",
    //            "elementType": "all",
    //            "stylers": [{ "visibility": "simplified" }]
    //        }, {
    //            "featureType": "road.highway",
    //            "elementType": "geometry.fill",
    //            "stylers": [{ "color": "#f4d2c5" }, { "visibility": "simplified" }]
    //        }, {
    //            "featureType": "road.highway",
    //            "elementType": "labels.text",
    //            "stylers": [{ "color": "#4e4e4e" }]
    //        }, {
    //            "featureType": "road.arterial",
    //            "elementType": "geometry.fill",
    //            "stylers": [{ "color": "#f4f4f4" }]
    //        }, {
    //            "featureType": "road.arterial",
    //            "elementType": "labels.text.fill",
    //            "stylers": [{ "color": "#787878" }]
    //        }, {
    //            "featureType": "road.arterial",
    //            "elementType": "labels.icon",
    //            "stylers": [{ "visibility": "off" }]
    //        }, { "featureType": "transit", "elementType": "all", "stylers": [{ "visibility": "off" }] },
    //        {
    //            "featureType": "water",
    //            "elementType": "all",
    //            "stylers": [{ "color": "#eaf6f8" }, { "visibility": "on" }]
    //        }, {
    //            "featureType": "water",
    //            "elementType": "geometry.fill",
    //            "stylers": [{ "color": "#eaf6f8" }]
    //        }
    //    ];

    //    gMap.setOptions({
    //        styles: styles
    //    });

    //    var pathLatLng: Model.CoordsModel[] = [];

    //    var previousPlot: IPlot = null;

    //    var currentPath: google.maps.LatLng[] = [];

    //    for (var i = 0; i < vm.plots.length; i++) {

    //        var plot = vm.plots[i];

    //        if (plot) {
    //            plot.color = this.plotHelper.getColorForSpeed(plot.Velocity);

    //            if (previousPlot && plot.color !== previousPlot.color) {

    //                var p: google.maps.PolylineOptions = {
    //                    path: currentPath,
    //                    strokeColor: previousPlot.color || plot.color, // i.e. not set
    //                    strokeWeight: 7,
    //                    strokeOpacity: 0.5,
    //                    map: gMap
    //                };

    //                var polyline = new google.maps.Polyline(p);

    //                const createHandler = (polyline, plot, mouseover) => {
    //                    return (x, y, z) => {
    //                        polyline.setOptions({
    //                            strokeOpacity: mouseover ? 1 : 0.5
    //                        });

    //                        var projection = gMap.getProjection();
    //                        var point = projection.fromLatLngToPoint(plot.pathCoords);
    //                        console.log(point);

    //                        if (mouseover) {
    //                            $("#map-tooltip").show().html(`<div class="map-marker reason${plot.Reason} hasDirection"><div class="map-marker-icon" style="transform: rotate(${plot.Direction + 45}deg)"></div><div class="map-marker-label">${plot.VelocityMph}</div></div>`);
    //                            $("#map-tooltip")
    //                                .css({
    //                                    "z-index": 1000,
    //                                    position: "absolute",
    //                                    top: point.y,
    //                                    left: point.x
    //                                });
    //                        } else {
    //                            $("#map-tooltip").hide();

    //                        }
    //                    }
    //                };

    //                polyline.addListener("mouseover", createHandler(polyline, plot, true));
    //                polyline.addListener("mouseout", createHandler(polyline, plot, false));

    //                this.myPaths.push(polyline);

    //                //polyline.setMap(gMap);

    //                currentPath = [];
    //                if (previousPlot) {
    //                    currentPath.push(new google.maps.LatLng(previousPlot.Latitude, previousPlot.Longitude));
    //                }
    //            }

    //            var coords = new CoordsModel(plot.Latitude, plot.Longitude);

    //            plot.pathCoords = new google.maps.LatLng(plot.Latitude, plot.Longitude);

    //            currentPath.push(plot.pathCoords);

    //            // this is for normal
    //            pathLatLng.push(coords);

    //            plot.coords = angular.copy(coords);
    //            plot.color = this.plotHelper.getColorForSpeed(plot.VelocityMph);
    //            //plot.options = this.getVehicleMarkerOptions(plot, vehicle, vm);

    //            plot.ID = i; //use the to keep the map markers directive happy
    //        }

    //        previousPlot = plot;
    //    }

    //    console.log(this.myPaths);

    //    //this.DisplaySelectedVehicleRouteOnMap(vm, pathLatLng);


    //    //var latLngList = this.convertPlotsToGoogleLatLng(pathLatLngList);


    //    //var options: google.maps.PolylineOptions = {
    //    //    path: latLngList,
    //    //    strokeColor: "#7D7BF2",
    //    //    strokeWeight: 5,
    //    //    strokeOpacity: 0.8
    //    //};

    //    //var polyline: google.maps.Polyline = new google.maps.Polyline(options);

    //    //return polyline;

    //}
}
